import { createContext, useContext, useId, useLayoutEffect, useMemo, useRef, type ReactNode, type RefObject } from "react";
import { useSidebarStore } from "@/atoms/playground";

type HistorySidebarRefs = {
  id: string;
  panel: RefObject<HTMLDivElement>;
  opener: RefObject<HTMLButtonElement>;
  closer: RefObject<HTMLButtonElement>;
};
const HistorySidebarContext = createContext<HistorySidebarRefs | null>(null);
export const useHistorySidebarAccessibility = () => useContext(HistorySidebarContext);

export function isAvailableHistoryTrigger(target: HTMLButtonElement | null): target is HTMLButtonElement {
  if (!target?.isConnected || target.ownerDocument !== document ||
    target.matches(":disabled, [aria-disabled='true']") ||
    target.closest("[hidden], [inert], [aria-hidden='true'], [aria-disabled='true']")) return false;
  for (let node: HTMLElement | null = target; node; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (style.display === "none" || style.visibility === "hidden" ||
      style.visibility === "collapse" || style.contentVisibility === "hidden" || style.opacity === "0") return false;
  }
  return Array.from(target.getClientRects()).some(rect => rect.width > 0 && rect.height > 0);
}

/** Local to one Playground layout; never locates focus targets by label. */
export function HistorySidebarAccessibility({ children, scopeKey }: { children: ReactNode; scopeKey: string }) {
  const id = useId();
  const panel = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  const closer = useRef<HTMLButtonElement>(null);
  const isOpen = useSidebarStore(state => state.isOpen);
  const committedScope = useRef(scopeKey);
  const pending = useRef<{ focused: Element; scope: string; opening: boolean } | null>(null);
  const value = useMemo(() => ({ id, panel, opener, closer }), [id]);

  useLayoutEffect(() => {
    // Register before the store's passive React subscriptions. Capture ownership
    // before hidden/inert DOM updates can move native focus to the body.
    const unsubscribe = useSidebarStore.subscribe((next, previous) => {
      pending.current = null;
      const focused = document.activeElement;
      if (previous.isOpen && !next.isOpen && focused && panel.current?.contains(focused)) {
        pending.current = { focused, scope: committedScope.current, opening: false };
      } else if (!previous.isOpen && next.isOpen && focused && focused === opener.current) {
        pending.current = { focused, scope: committedScope.current, opening: true };
      }
    });
    return () => { unsubscribe(); pending.current = null; };
  }, []);

  useLayoutEffect(() => {
    committedScope.current = scopeKey;
    const request = pending.current;
    pending.current = null;
    if (!request || request.opening !== isOpen) return;
    const focused = document.activeElement;
    const ownsFocus = focused === request.focused || focused === document.body || Boolean(focused && panel.current?.contains(focused));
    if (!ownsFocus) return;
    const target = isOpen ? closer.current : opener.current;
    if (request.scope === scopeKey && isAvailableHistoryTrigger(target)) {
      target.focus({ preventScroll: true });
    } else if (!isOpen && focused instanceof HTMLElement && panel.current?.contains(focused)) {
      // No eligible paired opener: release clipped focus without choosing an
      // unrelated control or stealing another overlay's focus.
      focused.blur();
    }
  }, [isOpen, scopeKey]);

  return <HistorySidebarContext.Provider value={value}>{children}</HistorySidebarContext.Provider>;
}
